/**
 * Point d'entrée : PWA autonome (#adlt-app) OU composants montés par les
 * shortcodes ([data-adlt-component]) — code React mutualisé (§ 11.1).
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';
import App from './App.jsx';
import { api } from './lib/api.js';
import { Spinner, Toast } from './components/common.jsx';
import { MesActivites, MesGroupes, MesInscriptions, Preferences } from './views/MemberViews.jsx';
import { ActiviteDetail, ActiviteCreer, ActiviteInscrits, ActivitesAvecDetail } from './views/ActivitesViews.jsx';
import { MoniteurCalendrier, MoniteurGroupe, MoniteurPresences } from './views/MoniteurViews.jsx';
import { Clubhouse, ClubhouseValidation } from './views/ClubhouseViews.jsx';
import { Admissions, Candidature } from './views/AdmissionsViews.jsx';
import { ClubhousePublic, DemandeAnimation, DemandeInitiation, Demandes, ListeAnimations, ListeInitiations, MesAnimations } from './views/PublicViews.jsx';
import { Actualites, ContactsComite, Equipe, GroupesHoraires, ClubhouseMembre, MesReservations } from './views/ExtrasViews.jsx';

const CFG = window.ADLT_CONFIG || {};

/** Enveloppe des composants embarqués : profil + toasts. */
function Embed({ component: Component, attrs }) {
  const [me, setMe] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  const publicComponent = [Candidature, ClubhousePublic, DemandeAnimation, DemandeInitiation, Equipe, GroupesHoraires].includes(Component);
  React.useEffect(() => {
    if (!publicComponent) api('me', { cache: 'me' }).then((r) => r.ok && setMe(r.data));
  }, [publicComponent]);
  if (!publicComponent && !me) return <Spinner />;
  const notifier = (message, error) => setToast({ message, error, id: Date.now() });
  return (
    <div className="adlt-app adlt-embed">
      <Component me={me} toast={notifier} refreshMe={() => api('me').then((r) => r.ok && setMe(r.data))}
        onOpen={() => {}} onBack={() => {}} onOpenActivite={() => {}} onDone={() => {}}
        {...attrs} />
      {toast && <Toast message={toast.message} error={toast.error} id={toast.id} onDone={() => setToast(null)} />}
    </div>
  );
}

const COMPOSANTS = {
  // Clé historique conservée : les pages WordPress publiées avec
  // [adlt_mon_calendrier] montent désormais l'agenda « Mes Activités ».
  mon_calendrier: MesActivites,
  mes_groupes: MesGroupes,
  mes_animations: MesAnimations,
  activites: ActivitesAvecDetail,
  activite: ActiviteDetail,
  mes_inscriptions: MesInscriptions,
  clubhouse: Clubhouse,
  moniteur_calendrier: MoniteurCalendrier,
  moniteur_groupe: MoniteurGroupe,
  moniteur_presences: MoniteurPresences,
  activite_creer: ActiviteCreer,
  activite_inscrits: ActiviteInscrits,
  clubhouse_validation: ClubhouseValidation,
  admissions: Admissions,
  candidature: Candidature,
  preferences: Preferences,
  clubhouse_public: ClubhousePublic,
  clubhouse_membre: ClubhouseMembre,
  demande_animation: DemandeAnimation,
  demande_initiation: DemandeInitiation,
  demandes: Demandes,
  equipe: Equipe,
  groupes_horaires: GroupesHoraires,
  actualites: Actualites,
  contacts_comite: ContactsComite,
  liste_animations: ListeAnimations,
  liste_initiations: ListeInitiations,
  mes_reservations: MesReservations,
};

// PWA autonome.
const rootEl = document.getElementById('adlt-app');
if (rootEl) {
  createRoot(rootEl).render(<App />);
}

// Composants embarqués dans les shortcodes.
document.querySelectorAll('[data-adlt-component]').forEach((el) => {
  const name = el.getAttribute('data-adlt-component');
  const Component = COMPOSANTS[name];
  if (!Component) return;
  let attrs = {};
  try { attrs = JSON.parse(el.getAttribute('data-adlt-attrs') || '{}'); } catch { /* attributs invalides ignorés */ }
  if (attrs.id) attrs.id = parseInt(attrs.id, 10);
  if (attrs.groupeId) attrs.groupeId = parseInt(attrs.groupeId, 10);
  createRoot(el).render(<Embed component={Component} attrs={attrs} />);
});

// Enregistrement du service worker (PWA autonome uniquement).
if (rootEl && 'serviceWorker' in navigator && CFG.swUrl) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(CFG.swUrl, { scope: CFG.swScope || '/' }).catch(() => {});
  });
}
